import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Eyebrow } from "@/components/Eyebrow";
import { PINNED_HEADER_TOP } from "@/lib/site";

const WEIGHTS = [
    { title: "Bleeding Labor.", line: "Hours every week spent on work software should be doing." },
    { title: "AI paralysis.", line: "Everyone agrees you should use it. Nobody knows how." },
    { title: "Tool sprawl.", line: "Dozen apps that don't talk to each other." },
    { title: "Deck fatigue.", line: "The consultants left a strategy, nobody built anything." },
    { title: "Dev shop burn.", line: "Quotes that start at fifty grand and land 6 months late." },
    { title: "No one to call.", line: "Something breaks, and there is no AI person on staff." },
];

const PAYOFF = "Or you could hand the pile to a team built to carry it.";
const WEDGE = "What to use, what to skip, and what will quietly burn you. That is the question all owners need answered.";

const CARD_W = 560;
const STEP_Y = 72;
const HEADLINE = "The AI solutions market grows by the hour.";

function PileCard({ item, index, progress }) {
    const restY = index * STEP_Y - ((WEIGHTS.length - 1) * STEP_Y) / 2;
    const offX = index % 2 === 0 ? -14 : 14;
    const rot = index % 2 === 0 ? -0.6 : 0.6;

    // cards begin almost immediately after the pin engages
    const start = 0.03 + index * 0.075;
    const y = useTransform(progress, [start, start + 0.06], [restY + 40, restY]);
    const opacity = useTransform(progress, [start, start + 0.05], [0, 1]);

    // per-card title strike, one at a time top to bottom, finishing around 0.76
    const strikeStart = 0.56 + index * 0.033;
    const strikeScale = useTransform(progress, [strikeStart, strikeStart + 0.03], [0, 1]);

    return (
        <motion.div
            style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                width: CARD_W,
                marginLeft: -CARD_W / 2,
                marginTop: 40,
                x: offX,
                y,
                rotate: rot,
                opacity,
            }}
            className="max-w-[80vw]"
        >
            <div className="rounded-2xl border border-border bg-background px-7 py-6">
                <div className="flex items-baseline gap-4">
                    <span className="font-mono-t text-xs text-muted-foreground">0{index + 1}</span>
                    <div>
                        <h3 className="relative inline-block font-display font-semibold text-xl tracking-tight">
                            {item.title}
                            <motion.span
                                aria-hidden
                                className="absolute left-[-6px] right-[-6px] top-1/2 h-[2px] origin-left rounded-full bg-foreground"
                                style={{ scaleX: strikeScale, translateY: "-50%" }}
                            />
                        </h3>
                        <p className="text-muted-foreground mt-1 leading-relaxed">{item.line}</p>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

function DesktopWeight() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

    const stackOpacity = useTransform(scrollYProgress, [0.78, 0.86], [1, 0]);
    const payoffOpacity = useTransform(scrollYProgress, [0.88, 1], [0, 1]);
    const payoffY = useTransform(scrollYProgress, [0.88, 1], [30, 0]);

    return (
        <div ref={ref} className="hidden lg:block h-[320vh]">
            <div className="sticky top-0 h-screen overflow-hidden flex items-center justify-center">
                {/* pinned header, always visible so the screen is never empty */}
                <div className={`absolute ${PINNED_HEADER_TOP} left-1/2 -translate-x-1/2 z-10 max-w-6xl w-full px-8`}>
                    <Eyebrow>The problem</Eyebrow>
                    <h2 className="mt-4 max-w-4xl font-display font-semibold tracking-[-0.02em] text-3xl leading-[1.12]">
                        {HEADLINE.replace(/\.$/, "")}<span className="period">.</span>
                    </h2>
                    <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">{WEDGE}</p>
                </div>

                <motion.div style={{ opacity: stackOpacity }} className="absolute inset-0">
                    {WEIGHTS.map((item, i) => (
                        <PileCard key={i} item={item} index={i} progress={scrollYProgress} />
                    ))}
                </motion.div>

                <motion.p
                    style={{ opacity: payoffOpacity, y: payoffY }}
                    className="relative max-w-4xl mx-auto px-8 text-center font-display font-semibold tracking-[-0.02em] text-4xl lg:text-5xl leading-[1.1]"
                >
                    {PAYOFF.replace(".", "")}<span className="period">.</span>
                </motion.p>
            </div>
        </div>
    );
}

function StackedWeight({ className }) {
    return (
        <div className={className}>
            <div className="space-y-3">
                {WEIGHTS.map((item, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, delay: i * 0.04 }}
                        className="rounded-2xl border border-border bg-background px-5 py-5"
                    >
                        <div className="flex items-baseline gap-3">
                            <span className="font-mono-t text-xs text-muted-foreground">0{i + 1}</span>
                            <div>
                                <h3 className="font-display font-semibold text-lg tracking-tight">{item.title}</h3>
                                <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{item.line}</p>
                            </div>
                        </div>
                    </motion.div>
                ))}
                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="pt-6 font-display font-semibold tracking-[-0.02em] text-3xl leading-[1.12]"
                >
                    {PAYOFF.replace(".", "")}<span className="period">.</span>
                </motion.p>
            </div>
        </div>
    );
}

function InFlowHeader({ className }) {
    return (
        <div className={className}>
            <Eyebrow>The problem</Eyebrow>
            <h2 className="mt-5 max-w-4xl font-display font-semibold tracking-[-0.02em] text-4xl sm:text-5xl leading-[1.08]">
                {HEADLINE.replace(/\.$/, "")}<span className="period">.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">{WEDGE}</p>
        </div>
    );
}

export function Weight() {
    const reduce = useReducedMotion();

    if (reduce) {
        return (
            <section id="weight" data-testid="weight" className="py-20 sm:py-24">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <InFlowHeader />
                    <StackedWeight className="mt-10" />
                </div>
            </section>
        );
    }

    return (
        <section id="weight" data-testid="weight" className="py-20 sm:py-24 lg:py-0">
            {/* mobile: in-flow header + stacked list */}
            <div className="lg:hidden max-w-6xl mx-auto px-5 sm:px-8">
                <InFlowHeader />
                <StackedWeight className="mt-10" />
            </div>
            {/* desktop: pinned scroll with header inside the sticky viewport */}
            <DesktopWeight />
        </section>
    );
}

